/**
 * @module builder
 */
import type { QuoteDocument, WorkOrderCardLayout, WorkOrderViewLayout } from 'paintscout'

/** The two work-order card grids, each with its own layout. */
export type WorkOrderLayoutView = 'area' | 'substrate'

/**
 * Everything below reads and writes `quote.workOrderLayout` only. The work order's card layout —
 * order, width, hidden — belongs to the work order alone: it must never reach `quote.order`, which
 * the estimate renders from, or dragging a work-order card would move estimate rows (ENG-1433).
 */

/** One view's saved layout ({} when it has none), for reading. */
export function getWorkOrderViewLayout(quote: QuoteDocument, view: WorkOrderLayoutView): WorkOrderViewLayout {
  return quote.workOrderLayout?.[view] ?? {}
}

/** One view's per-card overrides ({} when it has none), keyed by renderable-item key. */
export function getWorkOrderCardLayouts(
  quote: QuoteDocument,
  view: WorkOrderLayoutView
): Record<string, WorkOrderCardLayout> {
  return getWorkOrderViewLayout(quote, view).cards ?? {}
}

/**
 * Sort work-order cards by the view's saved card order. Keys the saved order doesn't know about
 * (items added after the last drag) keep their estimate-relative order and follow the ones it does.
 */
export function applyWorkOrderCardOrder<T extends { key: string }>(items: T[], order?: string[]): T[] {
  if (!order?.length) {
    return items
  }
  const rank = new Map(order.map((key, index) => [key, index]))
  return items
    .map((item, index) => ({ item, index, rank: rank.get(item.key) ?? Infinity }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ item }) => item)
}

/**
 * Save a work-order card order for one view. `keys` is the new order of the cards in the grid that
 * was dragged; keys already saved for the view's *other* grid are kept behind them. Each grid ranks
 * only its own cards, so the two never interleave.
 */
export function setWorkOrderCardOrder(args: {
  quote: QuoteDocument
  view: WorkOrderLayoutView
  keys: string[]
}): QuoteDocument {
  const { quote, view, keys } = args
  const viewLayout = { ...getWorkOrderViewLayout(quote, view) }
  const dragged = new Set(keys)
  const others = (viewLayout.order ?? []).filter((key) => !dragged.has(key))
  viewLayout.order = [...keys, ...others]
  return writeViewLayout(quote, view, viewLayout)
}

/**
 * Merge a patch into one card's overrides. Pass `undefined` for a field to clear it. Empty entries,
 * an empty card map and an empty view entry are all pruned, so the layout only carries what the
 * estimator actually customized.
 */
export function setWorkOrderCardLayout(args: {
  quote: QuoteDocument
  view: WorkOrderLayoutView
  key: string
  patch: WorkOrderCardLayout
}): QuoteDocument {
  const { quote, view, key, patch } = args
  const viewLayout = { ...getWorkOrderViewLayout(quote, view) }
  const cards = { ...getWorkOrderCardLayouts(quote, view) }
  const entry: WorkOrderCardLayout = { ...(cards[key] ?? {}), ...patch }
  if (!entry.hidden) {
    delete entry.hidden
  }
  if (!entry.width) {
    delete entry.width
  }
  if (Object.keys(entry).length === 0) {
    delete cards[key]
  } else {
    cards[key] = entry
  }
  if (Object.keys(cards).length === 0) {
    delete viewLayout.cards
  } else {
    viewLayout.cards = cards
  }
  return writeViewLayout(quote, view, viewLayout)
}

/** Un-hide one work-order card, so it leaves the "Hidden items" panel and returns to the grid. */
export function restoreWorkOrderCard(args: {
  quote: QuoteDocument
  view: WorkOrderLayoutView
  key: string
}): QuoteDocument {
  return setWorkOrderCardLayout({ ...args, patch: { hidden: undefined } })
}

/**
 * Whether "Clean up" would change anything for a view: a saved card order to drop, or a width
 * override to drop. Hidden cards deliberately don't count — Clean up leaves them hidden, so a view
 * whose only customization is a hidden card has nothing to clean up.
 */
export function getWorkOrderViewHasCustomizations(quote: QuoteDocument, view: WorkOrderLayoutView): boolean {
  const viewLayout = quote.workOrderLayout?.[view]
  if (!viewLayout) {
    return false
  }
  const cards = viewLayout.cards ?? {}
  return !!viewLayout.order?.length || Object.keys(cards).some((key) => !!cards[key]?.width)
}

/**
 * "Clean up" a view: drop its card order and its width overrides, so the grid falls back to the
 * estimate's item order at the default card width. Hidden cards stay hidden — hiding a card is a
 * deliberate choice about what the crew sees, not layout clutter, and it has its own Restore action.
 */
export function cleanUpWorkOrderView(args: { quote: QuoteDocument; view: WorkOrderLayoutView }): QuoteDocument {
  const { quote, view } = args
  if (!quote.workOrderLayout?.[view]) {
    return quote
  }
  const cards = getWorkOrderCardLayouts(quote, view)
  const keptCards = Object.keys(cards).reduce<Record<string, WorkOrderCardLayout>>((acc, key) => {
    if (cards[key]?.hidden) {
      acc[key] = { hidden: true }
    }
    return acc
  }, {})
  return writeViewLayout(quote, view, Object.keys(keptCards).length > 0 ? { cards: keptCards } : {})
}

/**
 * Write one view's layout back onto the quote, dropping the view entirely once it holds no
 * customizations. The other view is left as it is.
 */
function writeViewLayout(quote: QuoteDocument, view: WorkOrderLayoutView, viewLayout: WorkOrderViewLayout) {
  const next = { ...(quote.workOrderLayout ?? {}) }
  if (!viewLayout.cards && !viewLayout.order?.length) {
    delete next[view]
  } else {
    next[view] = viewLayout
  }
  return { ...quote, workOrderLayout: next }
}
